/**
 * The shard shop tab: items bought with meteor shards. Same icon-only tiles
 * as the upgrades (they share the tile styles), with the name, price in
 * shards, effect and requirement in the tooltip. An item shows once its
 * prerequisite is owned; bought items move to an Owned section below as
 * check-marked tiles, except the ones that grant an inventory item, which
 * are spent rather than kept and show up in the Inventory tab instead.
 */

import { SHOP_ITEMS, SHOP_ITEMS_BY_ID } from '../data/shop';
import type { ShopItemDef } from '../data/shop';
import { hasShopItem, isShopItemAvailable, shopItemPrice } from '../game/economy';
import { formatEggs } from '../game/numbers';
import type { GameState } from '../game/state';
import { bindTooltip } from './tooltip';
import { addRowText } from './itemView';
import type { RowText } from './itemView';
import { itemIconUrlById } from './inventory';
import { createDrawer, drawerElement } from './drawer';

export interface ShardShopView {
  update(state: GameState): void;
}

interface Tile {
  def: ShopItemDef;
  button: HTMLButtonElement;
  owned: HTMLElement;
  lastVisible: boolean | null;
  lastAffordable: boolean | null;
  lastPrice: number | null;
  /** The words the tile shows as a row; the price climbs on repeat buys. */
  text: RowText;
  lastOwned: boolean | null;
}

/** The state the tooltips read; set on every update so prices stay current. */
let tooltipState: GameState | null = null;

/** What this tile costs right now — a repeatable tile climbs with each buy. */
const priceOf = (def: ShopItemDef): number =>
  tooltipState ? shopItemPrice(def, tooltipState) : def.price;

/**
 * Each item's 48px icon in public/art/icons/shop/, named after the id's
 * second half. A tile that grants an inventory item shows that item's own
 * icon instead, so the splice looks the same on the shelf as in the bag.
 */
function iconUrl(def: ShopItemDef): string {
  if (def.effect.kind === 'grantItem') return itemIconUrlById(def.effect.itemId);
  return `${import.meta.env.BASE_URL}art/icons/shop/${def.id.split(':')[1]}.png`;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function iconImage(def: ShopItemDef): HTMLImageElement {
  const img = document.createElement('img');
  img.className = 'upgrade__icon';
  img.alt = '';
  img.src = iconUrl(def);
  return img;
}

function buildTile(def: ShopItemDef, onBuy: (def: ShopItemDef) => void): Tile {
  const button = el('button', 'upgrade');
  button.type = 'button';
  button.hidden = true;
  button.setAttribute('aria-label', `${def.name}, ${def.price} shards: ${def.description}`);
  button.append(iconImage(def));
  const text = addRowText(button, def.name, def.description, {
    currency: 'shards',
    amount: String(def.price),
  });
  button.addEventListener('click', () => onBuy(def));
  const requires = def.requires ? SHOP_ITEMS_BY_ID.get(def.requires)?.name : undefined;
  bindTooltip(button, () => {
    const price = priceOf(def);
    return {
      icon: { src: iconUrl(def) },
      lines: [
        { text: def.name },
        { text: `${price} meteor shard${price === 1 ? '' : 's'}`, cost: true },
        { text: def.description, muted: true },
        ...(requires ? [{ text: `After: ${requires}`, muted: true }] : []),
      ],
    };
  });

  const owned = el('div', 'upgrade upgrade--owned');
  owned.tabIndex = 0;
  owned.hidden = true;
  owned.setAttribute('aria-label', `${def.name} (owned): ${def.description}`);
  owned.append(iconImage(def));
  bindTooltip(owned, () => ({
    icon: { src: iconUrl(def) },
    // No "Owned" line: the tile is under the Owned heading already.
    lines: [{ text: def.name }, { text: def.description, muted: true }],
  }));

  return {
    def,
    button,
    owned,
    lastVisible: null,
    lastAffordable: null,
    lastPrice: null,
    lastOwned: null,
    text,
  };
}

export function createShardShopView(
  root: HTMLElement,
  onBuy: (def: ShopItemDef) => void,
): ShardShopView {
  // The balance sits at the top of the tab, with the shard icon.
  const balance = el('p', 'shard-shop__balance');
  const balanceIcon = document.createElement('img');
  balanceIcon.className = 'shard-shop__icon';
  balanceIcon.src = `${import.meta.env.BASE_URL}art/icons/shard-32.png`;
  balanceIcon.alt = '';
  balanceIcon.width = 32;
  balanceIcon.height = 32;
  const balanceCount = el('span', 'shard-shop__count', '0');
  balance.append(balanceCount, balanceIcon, el('span', '', 'meteor shards'));
  let lastBalance = '';

  const empty = el('p', 'upgrades__empty', 'Break meteors in the yard for shards to spend here.');
  const list = el('div', 'upgrades__list');
  const ownedDrawer = createDrawer('Owned', 'shard-shop');
  const ownedList = ownedDrawer.list;
  root.replaceChildren(balance, empty, list, drawerElement(ownedDrawer));

  const tiles = SHOP_ITEMS.map((def) => buildTile(def, onBuy));
  for (const t of tiles) {
    list.append(t.button);
    ownedList.append(t.owned);
  }
  let lastAnyVisible: boolean | null = null;
  let lastAnyOwned: boolean | null = null;

  return {
    update(state) {
      tooltipState = state;
      const text = formatEggs(Math.floor(state.shards));
      if (text !== lastBalance) {
        lastBalance = text;
        balanceCount.textContent = text;
      }
      let anyVisible = false;
      let ownedCount = 0;
      for (const t of tiles) {
        // A tile that grants an item is a purchase, not a possession: what
        // was bought is in the Inventory, so it never joins Owned.
        const owned = t.def.effect.kind !== 'grantItem' && hasShopItem(t.def, state);
        if (owned) ownedCount++;
        if (owned !== t.lastOwned) {
          t.lastOwned = owned;
          t.owned.hidden = !owned;
        }

        const visible = isShopItemAvailable(t.def, state);
        anyVisible ||= visible;
        if (visible !== t.lastVisible) {
          t.lastVisible = visible;
          t.button.hidden = !visible;
        }
        if (!visible) continue;
        // A repeatable tile climbs with each buy, so the label follows it.
        const price = shopItemPrice(t.def, state);
        if (price !== t.lastPrice) {
          t.lastPrice = price;
          t.text.setCost(String(price));
          t.button.setAttribute(
            'aria-label',
            `${t.def.name}, ${price} shards: ${t.def.description}`,
          );
        }
        const affordable = state.shards >= price;
        if (affordable !== t.lastAffordable) {
          t.lastAffordable = affordable;
          t.button.disabled = !affordable;
        }
      }
      if (anyVisible !== lastAnyVisible) {
        lastAnyVisible = anyVisible;
        empty.hidden = anyVisible;
      }
      const anyOwned = ownedCount > 0;
      if (anyOwned !== lastAnyOwned) {
        lastAnyOwned = anyOwned;
        ownedDrawer.setPresent(anyOwned);
      }
      ownedDrawer.setCount(ownedCount);
    },
  };
}
